import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAutoScroll, calculateScrollRate } from '../hooks/useAutoScroll';
import { useFitFontSize } from '../hooks/useFitFontSize';
import type { Song, ApiError } from '../types';
import type { LineGroup } from './performance/types';
import { buildGroups } from './performance/buildGroups';
import { GroupDisplay } from './performance/GroupDisplay';
import { nextPromptableIndex, prevPromptableIndex, nearestPromptableIndex } from './performance/types';

/** Number of upcoming lyric groups shown with reduced opacity. */
const UPCOMING_PREVIEW_COUNT = 3;

export default function PerformanceView() {
  const { songId } = useParams<{ songId: string }>();
  const navigate = useNavigate();

  const [song, setSong] = useState<Song | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const lyricsElRef = useRef<HTMLDivElement | null>(null);

  // Fetch song data
  useEffect(() => {
    if (!songId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await apiClient.getSong(songId!);
        if (!cancelled) setSong(data);
      } catch (err: unknown) {
        if (!cancelled) {
          const apiErr = err as ApiError;
          setError(apiErr?.message ?? 'Failed to load song.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [songId]);

  // Listen for fullscreen changes
  useEffect(() => {
    function onFsChange() {
      setIsFullScreen(!!document.fullscreenElement);
    }
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const groups: LineGroup[] = useMemo(() => buildGroups(song?.lines ?? []), [song]);

  const totalGroups = groups.length;

  // Compute a font size that fits the longest line in the song
  const { fontSize: perfFontSize, ref: fitRef } = useFitFontSize(groups);

  // Combined ref callback for the lyrics div
  const lyricsRef = useCallback(
    (node: HTMLDivElement | null) => {
      lyricsElRef.current = node;
      fitRef(node);
    },
    [fitRef],
  );

  // ---- Auto-scroll integration ----

  const lineHeightEstimate = useRef(120);

  const maxScrollPosition = useMemo(() => {
    return Math.max(0, (totalGroups - 1) * lineHeightEstimate.current);
  }, [totalGroups]);

  const handlePositionChange = useCallback(
    (position: number) => {
      const idx = Math.round(position / lineHeightEstimate.current);
      const clamped = Math.max(0, Math.min(idx, totalGroups - 1));
      setCurrentGroupIndex(nearestPromptableIndex(groups, clamped));
    },
    [totalGroups, groups],
  );

  const { state: scrollState, start, pause, resume, stop, setPosition } = useAutoScroll(
    handlePositionChange,
    maxScrollPosition,
  );

  const hasTempo = typeof song?.tempo === 'number' && song.tempo > 0;
  const hasLength =
    (typeof song?.lengthMinutes === 'number' && song.lengthMinutes > 0) ||
    (typeof song?.lengthSeconds === 'number' && song.lengthSeconds > 0);
  const canAutoScroll = hasTempo && hasLength && totalGroups > 0;

  const handlePlay = useCallback(() => {
    if (!song || !canAutoScroll) return;
    const rate = calculateScrollRate(
      song.tempo!,
      song.lengthMinutes ?? 0,
      song.lengthSeconds ?? 0,
      maxScrollPosition,
    );
    if (rate <= 0) return;
    start(rate);
  }, [song, canAutoScroll, maxScrollPosition, start]);

  const handlePauseResume = useCallback(() => {
    if (scrollState.isPaused) resume();
    else if (scrollState.isScrolling) pause();
  }, [scrollState, pause, resume]);

  const handleStop = useCallback(() => {
    stop();
    setCurrentGroupIndex(0);
  }, [stop]);

  // ---- Manual navigation (prev / next) ----

  const handlePrev = useCallback(() => {
    const newIdx = prevPromptableIndex(groups, currentGroupIndex);
    setCurrentGroupIndex(newIdx);
    setPosition(newIdx * lineHeightEstimate.current);
  }, [currentGroupIndex, groups, setPosition]);

  const handleNext = useCallback(() => {
    const newIdx = nextPromptableIndex(groups, currentGroupIndex);
    setCurrentGroupIndex(newIdx);
    setPosition(newIdx * lineHeightEstimate.current);
  }, [currentGroupIndex, groups, setPosition]);

  // ---- Manual scroll override (mouse wheel / touch) ----

  useEffect(() => {
    const el = lyricsElRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1 : -1;
      setCurrentGroupIndex((prev) => {
        const next = delta > 0
          ? nextPromptableIndex(groups, prev)
          : prevPromptableIndex(groups, prev);
        setPosition(next * lineHeightEstimate.current);
        return next;
      });
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [groups, setPosition]);

  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartY.current === null) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 30) {
        const delta = diff > 0 ? 1 : -1;
        setCurrentGroupIndex((prev) => {
          const next = delta > 0
            ? nextPromptableIndex(groups, prev)
            : prevPromptableIndex(groups, prev);
          setPosition(next * lineHeightEstimate.current);
          return next;
        });
      }
      touchStartY.current = null;
    },
    [groups, setPosition],
  );

  // Keyboard navigation
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') { e.preventDefault(); handleNext(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); handlePrev(); }
      else if (e.key === ' ') {
        e.preventDefault();
        if (scrollState.isScrolling) pause();
        else if (scrollState.isPaused) resume();
        else if (canAutoScroll) handlePlay();
      } else if (e.key === 'Escape' && !document.fullscreenElement) {
        navigate(-1);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleNext, handlePrev, scrollState, pause, resume, canAutoScroll, handlePlay, navigate]);

  const toggleFullScreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch { /* blocked by browser */ }
  }, []);

  // Determine visible groups
  const visibleStart = currentGroupIndex;
  const visibleEnd = Math.min(totalGroups, currentGroupIndex + 4 + UPCOMING_PREVIEW_COUNT);

  // ---- Render ----

  if (loading) {
    return (
      <div className="perf-container" role="status">
        <p className="perf-loading">Loading song…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="perf-container" role="alert">
        <p className="perf-error">{error}</p>
        <button className="btn btn-secondary" onClick={() => navigate(-1)} type="button">
          Go Back
        </button>
      </div>
    );
  }

  if (!song || totalGroups === 0) {
    return (
      <div className="perf-container">
        <p className="perf-error">No lyrics available for this song.</p>
        <button className="btn btn-secondary" onClick={() => navigate(-1)} type="button">
          Go Back
        </button>
      </div>
    );
  }

  const isPlaying = scrollState.isScrolling;
  const isPaused = scrollState.isPaused;

  return (
    <div
      className="perf-container"
      ref={containerRef}
      role="region"
      aria-label={`Performance view: ${song.title ?? 'Untitled'}`}
    >
      <div className="perf-toolbar">
        <button
          className="btn btn-secondary perf-back-btn"
          onClick={() => navigate(-1)}
          type="button"
          aria-label="Exit performance view"
        >
          ✕
        </button>
        <span className="perf-song-title">{song.title ?? 'Untitled'}</span>
        <span className="perf-line-counter" aria-live="polite">
          {currentGroupIndex + 1} / {totalGroups}
        </span>
        <button
          className="btn btn-secondary perf-fs-btn"
          onClick={toggleFullScreen}
          type="button"
          aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
        >
          {isFullScreen ? '⊡' : '⛶'}
        </button>
      </div>

      <div
        className="perf-lyrics"
        ref={lyricsRef}
        style={{ '--perf-font-size': `${perfFontSize}px` } as React.CSSProperties}
        aria-live="polite"
        aria-atomic="false"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {groups.slice(visibleStart, visibleEnd).map((group, offset) => {
          const absIndex = visibleStart + offset;
          const isCurrent = absIndex === currentGroupIndex;
          const isUpcoming =
            absIndex > currentGroupIndex &&
            absIndex <= currentGroupIndex + UPCOMING_PREVIEW_COUNT;
          return (
            <GroupDisplay
              key={absIndex}
              group={group}
              isCurrent={isCurrent}
              isUpcoming={isUpcoming}
            />
          );
        })}
      </div>

      <div className="perf-controls">
        <button
          className="btn btn-secondary"
          onClick={handlePrev}
          disabled={currentGroupIndex === 0}
          type="button"
          aria-label="Previous line"
        >
          ▲ Prev
        </button>

        {canAutoScroll && !isPlaying && !isPaused && (
          <button
            className="btn btn-primary perf-play-btn"
            onClick={handlePlay}
            type="button"
            aria-label="Start auto-scroll"
          >
            ▶ Play
          </button>
        )}
        {(isPlaying || isPaused) && (
          <>
            <button
              className="btn btn-primary perf-pause-btn"
              onClick={handlePauseResume}
              type="button"
              aria-label={isPaused ? 'Resume auto-scroll' : 'Pause auto-scroll'}
            >
              {isPaused ? '▶ Resume' : '⏸ Pause'}
            </button>
            <button
              className="btn btn-danger perf-stop-btn"
              onClick={handleStop}
              type="button"
              aria-label="Stop auto-scroll and reset"
            >
              ⏹ Stop
            </button>
          </>
        )}

        {!canAutoScroll && !isPlaying && !isPaused && (
          <span className="perf-no-scroll-hint" aria-live="polite">
            Set tempo &amp; length to enable auto-scroll
          </span>
        )}

        <button
          className="btn btn-secondary"
          onClick={handleNext}
          disabled={currentGroupIndex >= totalGroups - 1}
          type="button"
          aria-label="Next line"
        >
          ▼ Next
        </button>
      </div>
    </div>
  );
}
